import React, { useState } from 'react';
import type { Checkpoint, DayItem } from '../lib/api';

// ─── Where a student is in their curriculum ──────────────────────────────────
// Days ticked off are done. The furthest one ticked is where they've reached;
// earlier days that aren't ticked are "not marked" (skipped, or the mentor
// forgot to tick them). Next is the first open day after the furthest one —
// or the first not-marked day once everything after it is done.

export type DayState = 'done' | 'next' | 'skipped' | 'upcoming';

export interface CurriculumProgress {
  total: number;
  done: number;
  pct: number;
  latest: DayItem | null;
  next: DayItem | null;       // null when every day is done
  skipped: DayItem[];
  stateOf: (day: number) => DayState;
}

export function curriculumProgress(days: DayItem[], completedDays: number[]): CurriculumProgress {
  const sorted   = [...days].sort((a, b) => a.day - b.day);
  const ticked   = new Set(completedDays);
  const doneDays = sorted.filter(d => ticked.has(d.day));
  const latest   = doneDays.length ? doneDays[doneDays.length - 1] : null;
  const skipped  = latest ? sorted.filter(d => d.day < latest.day && !ticked.has(d.day)) : [];
  const next     = sorted.find(d => !ticked.has(d.day) && (!latest || d.day > latest.day)) ?? skipped[0] ?? null;
  return {
    total: sorted.length,
    done:  doneDays.length,
    pct:   sorted.length ? Math.round((doneDays.length / sorted.length) * 100) : 0,
    latest, next, skipped,
    stateOf: day => ticked.has(day) ? 'done'
      : next && day === next.day ? 'next'
      : latest && day < latest.day ? 'skipped'
      : 'upcoming',
  };
}

// [1,2,3,5,8,9] → "D1–D3, D5, D8–D9"
export function dayRanges(nums: number[]): string {
  const sorted = Array.from(new Set(nums)).sort((a, b) => a - b);
  const parts: string[] = [];
  for (let i = 0; i < sorted.length; i++) {
    let j = i;
    while (j + 1 < sorted.length && sorted[j + 1] === sorted[j] + 1) j++;
    parts.push(j > i ? `D${sorted[i]}–D${sorted[j]}` : `D${sorted[i]}`);
    i = j;
  }
  return parts.join(', ');
}

// ─── Colours for the light pages and the dark popup ──────────────────────────

const TONES = {
  light: {
    text: '#1f1933', sub: '#6b7280', faint: '#9ca3af', line: '#ece9f3', soft: '#f8f7fb', card: '#ffffff',
    done: '#15803d', doneBg: '#dcfce7', doneBar: '#22c55e',
    next: '#6d28d9', nextBg: '#f3efff', nextBar: '#7c3aed',
    skip: '#b45309', skipBg: '#fef3c7', skipBar: '#f59e0b',
    upBar: '#e7e4ee',
    chip: '#2b2240', chipText: '#ffffff',
  },
  dark: {
    text: '#ffffff', sub: 'rgba(255,255,255,.55)', faint: 'rgba(255,255,255,.32)', line: 'rgba(255,255,255,.07)',
    soft: 'rgba(255,255,255,.04)', card: 'rgba(255,255,255,.03)',
    done: '#4ade80', doneBg: 'rgba(74,222,128,.14)', doneBar: '#4ade80',
    next: '#c4b5fd', nextBg: 'rgba(167,139,250,.14)', nextBar: '#a78bfa',
    skip: '#fbbf24', skipBg: 'rgba(251,191,36,.12)', skipBar: '#f59e0b',
    upBar: 'rgba(255,255,255,.1)',
    chip: '#F0B429', chipText: '#1a1330',
  },
};
type Tone = typeof TONES.light;

const PROJECT_LABEL: Record<string, string> = { locked: 'Locked', available: 'Unlocked', completed: 'Completed' };

// ─── Component ───────────────────────────────────────────────────────────────

export const CurriculumRoadmap: React.FC<{
  title?: string | null;
  days: DayItem[];
  completedDays: number[];
  checkpoints?: Checkpoint[];
  projectStatuses?: Array<{ phase: number; status: string }>;
  tone?: 'light' | 'dark';
}> = ({ title, days, completedDays, checkpoints = [], projectStatuses = [], tone = 'light' }) => {
  const c = TONES[tone];
  const [show, setShow] = useState<'all' | 'done' | 'left'>('all');

  if (days.length === 0) {
    return (
      <div style={{ padding: 16, borderRadius: 14, background: c.soft, border: `1px solid ${c.line}` }}>
        <div style={{ color: c.text, fontWeight: 700, fontSize: 13 }}>No curriculum for this course</div>
        <div style={{ color: c.sub, fontSize: 12, marginTop: 4 }}>
          Day titles show once the course matches one of the curricula (AIML, Data Science, Data Analytics, BA, Python Full Stack).
        </div>
        {completedDays.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginTop: 12 }}>
            {completedDays.map(d => (
              <span key={d} style={{ padding: '3px 8px', borderRadius: 6, background: c.doneBg, color: c.done, fontSize: 11, fontWeight: 800 }}>D{d}</span>
            ))}
          </div>
        )}
      </div>
    );
  }

  const p = curriculumProgress(days, completedDays);
  const sorted = [...days].sort((a, b) => a.day - b.day);
  const statusOf = new Map(projectStatuses.map(s => [s.phase, s.status]));
  const projectAfter = new Map<number, Checkpoint[]>();
  checkpoints.forEach(cp => projectAfter.set(cp.afterDay, [...(projectAfter.get(cp.afterDay) || []), cp]));

  const visible = sorted.filter(d => show === 'all' || (show === 'done') === (p.stateOf(d.day) === 'done'));
  const barColor = (s: DayState) => s === 'done' ? c.doneBar : s === 'next' ? c.nextBar : s === 'skipped' ? c.skipBar : c.upBar;
  const count: Record<DayState, number> = { done: 0, next: 0, skipped: 0, upcoming: 0 };
  sorted.forEach(d => { count[p.stateOf(d.day)]++; });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {/* Headline + day strip */}
      <div>
        <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ color: c.faint, fontSize: 10, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase' }}>Curriculum</div>
            <div style={{ color: c.text, fontWeight: 800, fontSize: 15, marginTop: 2 }}>{title || 'Course curriculum'}</div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <span style={{ color: c.done, fontWeight: 900, fontSize: 22 }}>{p.done}</span>
            <span style={{ color: c.sub, fontSize: 13, fontWeight: 600 }}> / {p.total} days · {p.pct}%</span>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 2, marginTop: 10 }}>
          {sorted.map(d => (
            <div key={d.day} title={`Day ${d.day} — ${d.topic}`}
              style={{ flex: 1, height: 8, borderRadius: 2, background: barColor(p.stateOf(d.day)) }} />
          ))}
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 14px', marginTop: 8 }}>
          {[
            { label: `Done ${count.done}`,             color: c.doneBar, hide: false },
            { label: 'Up next',                        color: c.nextBar, hide: count.next === 0 },
            { label: `Not marked ${count.skipped}`,    color: c.skipBar, hide: count.skipped === 0 },
            { label: `Upcoming ${count.upcoming}`,     color: c.upBar,   hide: count.upcoming === 0 },
          ].filter(l => !l.hide).map(l => (
            <span key={l.label} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, color: c.sub, fontSize: 11 }}>
              <span style={{ width: 8, height: 8, borderRadius: 2, background: l.color }} />{l.label}
            </span>
          ))}
        </div>
      </div>

      {/* Reached / next */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 10 }}>
        <Callout c={c} label="Reached" color={c.done} bg={c.doneBg}
          day={p.latest} empty="No days ticked off yet" />
        <Callout c={c} label="Up next" color={c.next} bg={c.nextBg}
          day={p.next} empty="Course complete 🎉" />
      </div>
      {p.skipped.length > 0 && (
        <div style={{ padding: '8px 12px', borderRadius: 10, background: c.skipBg, color: c.skip, fontSize: 12, lineHeight: 1.5 }}>
          <strong>{p.skipped.length} earlier day{p.skipped.length === 1 ? '' : 's'} not ticked:</strong> {dayRanges(p.skipped.map(d => d.day))}
        </div>
      )}

      {/* Filter */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {([
          ['all',  `All ${p.total}`],
          ['done', `Completed ${p.done}`],
          ['left', `Remaining ${p.total - p.done}`],
        ] as const).map(([k, label]) => (
          <button key={k} type="button" onClick={() => setShow(k)} style={{
            padding: '5px 12px', borderRadius: 999, fontSize: 11, fontWeight: 700, cursor: 'pointer',
            border: `1px solid ${show === k ? c.chip : c.line}`,
            background: show === k ? c.chip : 'transparent',
            color: show === k ? c.chipText : c.sub,
          }}>{label}</button>
        ))}
      </div>

      {/* Day by day */}
      <div style={{ borderRadius: 12, border: `1px solid ${c.line}`, background: c.card, overflow: 'hidden' }}>
        {visible.length === 0 && (
          <div style={{ padding: 18, textAlign: 'center', color: c.faint, fontSize: 12 }}>
            {show === 'done' ? 'No days ticked off yet' : 'Every day is done'}
          </div>
        )}
        {visible.map((d, i) => {
          const s = p.stateOf(d.day);
          const projects = show === 'all' ? projectAfter.get(d.day) || [] : [];
          return (
            <React.Fragment key={d.day}>
              <div style={{
                display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px',
                borderTop: i === 0 ? 'none' : `1px solid ${c.line}`,
                background: s === 'next' ? c.nextBg : 'transparent',
              }}>
                <span style={{
                  flexShrink: 0, minWidth: 38, textAlign: 'center', padding: '3px 0', borderRadius: 6,
                  fontSize: 11, fontWeight: 800,
                  background: s === 'done' ? c.doneBg : s === 'next' ? c.nextBar : s === 'skipped' ? c.skipBg : c.soft,
                  color: s === 'done' ? c.done : s === 'next' ? '#fff' : s === 'skipped' ? c.skip : c.faint,
                }}>D{d.day}</span>
                <span style={{
                  flex: 1, minWidth: 0, fontSize: 12.5, lineHeight: 1.4,
                  color: s === 'upcoming' ? c.sub : c.text, fontWeight: s === 'next' ? 700 : 500,
                }}>{d.topic}</span>
                {s === 'done'    && <Tag color={c.done} bg={c.doneBg}>✓ Done</Tag>}
                {s === 'next'    && <Tag color={c.next} bg="transparent" border={c.nextBar}>Up next</Tag>}
                {s === 'skipped' && <Tag color={c.skip} bg={c.skipBg}>Not marked</Tag>}
              </div>
              {projects.map(cp => {
                const st = statusOf.get(cp.phase) || 'locked';
                const col = st === 'completed' ? c.done : st === 'available' ? c.next : c.faint;
                return (
                  <div key={`p${cp.phase}`} style={{
                    display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px',
                    borderTop: `1px dashed ${c.line}`, background: c.soft,
                  }}>
                    <span style={{ flexShrink: 0, minWidth: 38, textAlign: 'center', fontSize: 14 }}>🏁</span>
                    <span style={{ flex: 1, minWidth: 0, fontSize: 12, lineHeight: 1.4, color: c.sub }}>
                      <strong style={{ color: c.text }}>Project {cp.phase}</strong>
                      {cp.difficulty && <> · {cp.difficulty}</>} — {cp.description}
                    </span>
                    <Tag color={col} bg="transparent" border={col}>{PROJECT_LABEL[st] || st}</Tag>
                  </div>
                );
              })}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};

const Callout: React.FC<{ c: Tone; label: string; color: string; bg: string; day: DayItem | null; empty: string }> =
  ({ c, label, color, bg, day, empty }) => (
    <div style={{ padding: '10px 12px', borderRadius: 12, background: bg, minWidth: 0 }}>
      <div style={{ color, fontSize: 10, fontWeight: 800, letterSpacing: 1, textTransform: 'uppercase' }}>{label}</div>
      {day ? (
        <div style={{ marginTop: 3, color: c.text, fontSize: 13, fontWeight: 600, lineHeight: 1.4 }}>
          <span style={{ color, fontWeight: 800 }}>Day {day.day}</span> — {day.topic}
        </div>
      ) : (
        <div style={{ marginTop: 3, color: c.sub, fontSize: 13 }}>{empty}</div>
      )}
    </div>
  );

const Tag: React.FC<{ color: string; bg: string; border?: string; children: React.ReactNode }> = ({ color, bg, border, children }) => (
  <span style={{
    flexShrink: 0, padding: '2px 8px', borderRadius: 6, fontSize: 10, fontWeight: 800, whiteSpace: 'nowrap',
    color, background: bg, border: border ? `1px solid ${border}` : 'none',
  }}>{children}</span>
);
